/**
 * CW-SF-M5 TKT-5 — "Closing This Quarter" widget.
 *
 * Open opps with CloseDate in the current calendar quarter (fiscal
 * deferred to M6). Past-due items styled red. Subtotal at top.
 */
import { useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { formatCurrency, type SfWidgetEnvelope } from './sfWidgetUtils';

interface OppItem {
  external_file_id: string;
  name: string;
  stage: string | null;
  amount: number | null;
  close_date: string | null;
  past_due: boolean;
  web_url: string | null;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: OppItem[];
  quarter_label: string | null;
  committed_amount: number;
}

const REFRESH_S = 5 * 60;

export function SfClosingThisQuarterWidget(_props: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>('/mod/cw/widgets/sf/closing-this-quarter'),
    [],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--quarter">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.items?.length ?? 0) > 0}
        emptyMessage={`No opportunities closing in ${data?.quarter_label ?? 'this quarter'}.`}
      >
        <div className="sf-widget__header-row">
          <span className="sf-widget__quarter-label">{data?.quarter_label}</span>
          <span className="sf-widget__quarter-total">
            {formatCurrency(data?.committed_amount ?? 0)}
          </span>
        </div>
        <ul className="sf-widget__list">
          {data?.items.map((o) => (
            <li
              key={o.external_file_id}
              className={
                o.past_due ? 'sf-widget__row sf-widget__row--overdue' : 'sf-widget__row'
              }
            >
              <a
                href={o.web_url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="sf-widget__row-name"
              >
                {o.name}
                <ExternalLink size={10} />
              </a>
              <div className="sf-widget__row-chips">
                {o.stage && <span className="sf-widget__chip">{o.stage}</span>}
                {o.amount !== null && (
                  <span className="sf-widget__chip sf-widget__chip--amount">
                    {formatCurrency(o.amount)}
                  </span>
                )}
                {o.close_date && (
                  <span className="sf-widget__chip">{o.close_date}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
